import { useCallback, useEffect, useState } from 'react';
import { NavigationGuardDialogProps, NavigationGuardResult, UseNavigationGuardOptions } from 'types/Dialog.types';

/**
 * Hook that provides navigation confirmation functionality for both programmatic and browser navigation
 */
const useNavigationGuard = ({
  dialogTitle,
  dialogText,
  cancelText,
  confirmText,
  additionalText,
  hasUnsavedChanges,
}: UseNavigationGuardOptions): NavigationGuardResult => {
  const [showDialog, setShowDialog] = useState(false);
  const [onConfirmCallback, setOnConfirmCallback] = useState<(() => void) | null>(null);

  const showConfirmDialog = useCallback((onConfirm: () => void) => {
    setOnConfirmCallback(() => onConfirm);
    setShowDialog(true);
  }, []);

  const handleConfirm = useCallback(() => {
    setShowDialog(false);
    if (onConfirmCallback) {
      onConfirmCallback();
    }
    setOnConfirmCallback(null);
  }, [onConfirmCallback]);

  const handleCancel = useCallback(() => {
    setShowDialog(false);
    setOnConfirmCallback(null);
  }, []);

  // Handle browser navigation (refresh, close, back/forward)
  useEffect(() => {
    let isShowingCustomDialog = false;
    let contextMenuTimeout: ReturnType<typeof setTimeout> | undefined;
    let pendingBeforeUnloadHandler: ((e: BeforeUnloadEvent) => string | undefined) | undefined;

    // Handle keyboard shortcuts for refresh
    const handleKeyDown = (event: KeyboardEvent) => {
      const isRefresh =
        (event.ctrlKey && event.key === 'r') || (event.metaKey && event.key === 'r') || event.key === 'F5';

      if (isRefresh && hasUnsavedChanges() && !isShowingCustomDialog) {
        event.preventDefault();
        event.stopPropagation();
        isShowingCustomDialog = true;

        showConfirmDialog(() => {
          isShowingCustomDialog = false;
          window.location.reload();
        });
      }
    };

    const handleContextMenu = () => {
      if (contextMenuTimeout) clearTimeout(contextMenuTimeout);
      if (pendingBeforeUnloadHandler) {
        window.removeEventListener('beforeunload', pendingBeforeUnloadHandler);
        pendingBeforeUnloadHandler = undefined;
      }
      contextMenuTimeout = setTimeout(() => {
        const handleBeforeUnloadFromContext = (e: BeforeUnloadEvent) => {
          pendingBeforeUnloadHandler = undefined;
          if (hasUnsavedChanges() && !isShowingCustomDialog) {
            e.preventDefault();
            isShowingCustomDialog = true;

            showConfirmDialog(() => {
              isShowingCustomDialog = false;
              window.location.reload();
            });

            return '';
          }
          return undefined;
        };

        pendingBeforeUnloadHandler = handleBeforeUnloadFromContext;
        window.addEventListener('beforeunload', pendingBeforeUnloadHandler, { once: true });
      }, 100);
    };

    const handleBeforeUnload = (event: BeforeUnloadEvent) => {
      if (hasUnsavedChanges() && !isShowingCustomDialog) {
        // For cases we can't intercept (like browser refresh button),
        // we still need the browser dialog as fallback
        event.preventDefault();
        return dialogText;
      }
      return undefined;
    };

    document.addEventListener('keydown', handleKeyDown, true);
    document.addEventListener('contextmenu', handleContextMenu);
    window.addEventListener('beforeunload', handleBeforeUnload);

    return () => {
      if (contextMenuTimeout) clearTimeout(contextMenuTimeout);
      if (pendingBeforeUnloadHandler) {
        window.removeEventListener('beforeunload', pendingBeforeUnloadHandler);
      }
      document.removeEventListener('keydown', handleKeyDown, true);
      document.removeEventListener('contextmenu', handleContextMenu);
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [hasUnsavedChanges, dialogText, showConfirmDialog]);

  // Handle browser back/forward navigation
  useEffect(() => {
    if (!hasUnsavedChanges()) return undefined;

    const handlePopState = () => {
      if (hasUnsavedChanges()) {
        // Prevent multiple rapid pushState calls
        if (window.history.state !== 'navigation-guard') {
          window.history.pushState('navigation-guard', '', window.location.href);
        }

        showConfirmDialog(() => {
          window.history.back();
        });
      }
    };

    // Only push state if we haven't already
    if (window.history.state !== 'navigation-guard') {
      window.history.pushState('navigation-guard', '', window.location.href);
    }
    window.addEventListener('popstate', handlePopState);

    return () => {
      window.removeEventListener('popstate', handlePopState);
    };
  }, [hasUnsavedChanges, showConfirmDialog]);

  const navigationDialogProps: NavigationGuardDialogProps = {
    open: showDialog,
    dialogTitle,
    dialogText,
    additionalText,
    confirmText,
    cancelText,
    onConfirm: handleConfirm,
    onCancel: handleCancel,
  };

  return {
    showConfirmDialog,
    navigationDialogProps,
  };
};

export default useNavigationGuard;
